/**
 * Organization and Multi-Tenancy Types
 */

import type { BillingSettings } from '@/types/billing';
import type { ReferralCustomField } from '@/types/referral';

export type OrganizationStatus = 'active' | 'suspended' | 'inactive';

export type InvitationChannelMode = 'email_only' | 'sms_only' | 'email_or_sms';

export interface DeactivationMessagesSettings {
  agent_message?: string;
  customer_message?: string;
}

export interface InviteDefaultFieldConfig {
  enabled: boolean;
  required: boolean;
  /** Optional display-label override for this field (falls back to the default label). */
  label?: string;
}

export interface InviteDefaultFieldsSettings {
  /** First name standard field (replaces the legacy single `name` field). */
  first_name?: InviteDefaultFieldConfig;
  /** Last name standard field (replaces the legacy single `name` field). */
  last_name?: InviteDefaultFieldConfig;
  email?: { required: boolean; label?: string };
  phone?: { required: boolean; label?: string };
  /**
   * @deprecated Legacy single Name field. Retained read-only for back-compat
   * migration to `first_name`/`last_name` via `migrateLegacyName`.
   */
  name?: InviteDefaultFieldConfig;
}

/** The four built-in (standard) invite-form fields, referenced by the layout. */
export type InviteStandardKey = 'first_name' | 'last_name' | 'email' | 'phone';

/**
 * A single entry in the invite-form field layout: either a built-in standard
 * field (by key) or a reference to a custom field (by its
 * `referral_custom_fields[].id`).
 */
export type InviteFieldRef =
  | { kind: 'standard'; key: InviteStandardKey }
  | { kind: 'custom'; id: string };

/** Full ordered sequence of invite-form fields (standard + custom interleaved). */
export type InviteFieldLayout = InviteFieldRef[];

/**
 * Bulk customer invite method for a child organisation (GitLab #720).
 *
 * - `workflow_first` (default) — the admin picks a single workflow before
 *   uploading; the CSV has no workflow column.
 * - `csv_workflow` — a single CSV can invite customers to different workflows;
 *   a mandatory Workflow column identifies each row's workflow.
 */
export type BulkInviteMethod = 'workflow_first' | 'csv_workflow';

export const DEFAULT_BULK_INVITE_METHOD: BulkInviteMethod = 'workflow_first';

/**
 * How the customer-facing sign-up surfaces capture a name (#819 / #795):
 * the branded self-registration page and the invitation landing page.
 *
 *   - `full_name`   — one combined "Full name" field (the historical shape).
 *   - `first_last`  — separate "First name" and "Last name" fields.
 *   - `first_only`  — a single "First name" field; no surname is captured.
 */
export type CustomerNameCapture = 'full_name' | 'first_last' | 'first_only';

export const CUSTOMER_NAME_CAPTURE_MODES: readonly CustomerNameCapture[] = [
  'full_name',
  'first_last',
  'first_only',
];

export const DEFAULT_CUSTOMER_NAME_CAPTURE: CustomerNameCapture = 'full_name';

/** Narrow an unknown settings value to a valid capture mode, else the default. */
export function resolveCustomerNameCapture(value: unknown): CustomerNameCapture {
  return typeof value === 'string' &&
    (CUSTOMER_NAME_CAPTURE_MODES as readonly string[]).includes(value)
    ? (value as CustomerNameCapture)
    : DEFAULT_CUSTOMER_NAME_CAPTURE;
}

export interface ReferralTwilioSettings {
  alphanumeric_sender_id: string | null;
}

export const DEFAULT_REFERRAL_TWILIO_SETTINGS: ReferralTwilioSettings = {
  alphanumeric_sender_id: null,
};

/**
 * Per-child-org friendly sender name shown in the email "From" header
 * (e.g. `"Paylink" <noreply@…>`). The sending address itself is unchanged.
 * See lib/email/senderIdentity.ts.
 */
export interface EmailSenderSettings {
  from_name: string | null;
}

export const DEFAULT_EMAIL_SENDER_SETTINGS: EmailSenderSettings = {
  from_name: null,
};

/**
 * Per-child-org authentication challenge expiry preferences. Only the two
 * expiry knobs are exposed to child orgs; every value is clamped to safe
 * bounds by `normaliseAuthChallengeSettings` (see lib/auth/authChallengeSettings.ts).
 * Security-critical controls (attempt caps, lockout, per-number cost caps) are
 * deliberately NOT configurable per org.
 */
export interface AuthChallengeSettings {
  /** Email magic-link validity in minutes. Clamped 10–1440 (24h). */
  email_magic_link_expiry_minutes: number;
  /** SMS OTP code validity in minutes. Clamped 5–15. */
  sms_otp_expiry_minutes: number;
  /** Password-reset link validity in minutes. Clamped 10–1440 (24h). */
  password_reset_expiry_minutes: number;
  /** Minimum gap between consecutive SMS codes, in seconds. Clamped 10–3600. */
  sms_otp_resend_cooldown_seconds: number;
  /** Rolling window for the SMS send cap, in minutes. Clamped 1–1440. */
  sms_otp_send_window_minutes: number;
  /** Max SMS codes (initial + resends) per window. Clamped 1–20. */
  sms_otp_max_sends_per_window: number;
  /** Minimum gap between consecutive magic links, in seconds. Clamped 10–3600. */
  email_magic_link_resend_cooldown_seconds: number;
  /** Rolling window for the magic-link send cap, in minutes. Clamped 1–1440. */
  email_magic_link_send_window_minutes: number;
  /** Max magic links (initial + resends) per window. Clamped 1–20. */
  email_magic_link_max_sends_per_window: number;
}

export const DEFAULT_AUTH_CHALLENGE_SETTINGS: AuthChallengeSettings = {
  email_magic_link_expiry_minutes: 60,
  sms_otp_expiry_minutes: 10,
  password_reset_expiry_minutes: 60,
  sms_otp_resend_cooldown_seconds: 60,
  sms_otp_send_window_minutes: 60,
  sms_otp_max_sends_per_window: 5,
  email_magic_link_resend_cooldown_seconds: 60,
  email_magic_link_send_window_minutes: 60,
  email_magic_link_max_sends_per_window: 5,
};

export const AUTH_CHALLENGE_LIMITS = {
  email_magic_link_expiry_minutes: { min: 10, max: 1440 },
  sms_otp_expiry_minutes: { min: 5, max: 15 },
  password_reset_expiry_minutes: { min: 10, max: 1440 },
  sms_otp_resend_cooldown_seconds: { min: 10, max: 3600 },
  sms_otp_send_window_minutes: { min: 1, max: 1440 },
  sms_otp_max_sends_per_window: { min: 1, max: 20 },
  email_magic_link_resend_cooldown_seconds: { min: 10, max: 3600 },
  email_magic_link_send_window_minutes: { min: 1, max: 1440 },
  email_magic_link_max_sends_per_window: { min: 1, max: 20 },
} as const;

/**
 * Per-child-org customer authentication model (#652). Resolved with a safe
 * `passwordless` fallback by `getCustomerAuthModel`
 * (see lib/auth/customerAuthModel.ts).
 */
export type CustomerAuthModel =
  | 'passwordless'
  | 'password_plus_factor'
  | 'passwordless_plus_factor';

export const CUSTOMER_AUTH_MODELS: readonly CustomerAuthModel[] = [
  'passwordless',
  'password_plus_factor',
  'passwordless_plus_factor',
];

export const DEFAULT_CUSTOMER_AUTH_MODEL: CustomerAuthModel = 'passwordless';

/**
 * Per-child-org customer password policy (#652). Only used when the org's
 * `customer_auth_model` is `password_plus_factor`. Lengths are clamped by
 * `getCustomerPasswordPolicy` (see lib/auth/customerPasswordPolicy.ts). The
 * four character-type rules are locked on while Supabase Auth (GoTrue)
 * enforces `lower_upper_letters_digits_symbols` project-wide.
 */
export interface CustomerPasswordPolicy {
  min_length: number;
  max_length: number;
  require_uppercase: true;
  require_lowercase: true;
  require_number: true;
  require_special: true;
}

export const DEFAULT_CUSTOMER_PASSWORD_POLICY: CustomerPasswordPolicy = {
  min_length: 12,
  max_length: 72,
  require_uppercase: true,
  require_lowercase: true,
  require_number: true,
  require_special: true,
};

/** Hard bounds: 12 is the GoTrue floor, 72 bytes is the bcrypt cap. */
export const CUSTOMER_PASSWORD_POLICY_LIMITS = {
  min_length: { min: 12, max: 72 },
  max_length: { min: 12, max: 72 },
} as const;

export interface OrganizationSettings {
  invitation_channel_mode?: InvitationChannelMode;
  customer_auth_model?: CustomerAuthModel;
  customer_password_policy?: CustomerPasswordPolicy;
  referral_custom_fields?: ReferralCustomField[];
  invite_default_fields?: InviteDefaultFieldsSettings;
  invite_field_layout?: InviteFieldLayout;
  bulk_invite_method?: BulkInviteMethod;
  customer_name_capture?: CustomerNameCapture;
  referral_twilio_settings?: ReferralTwilioSettings;
  email_sender_settings?: EmailSenderSettings;
  auth_challenge_settings?: AuthChallengeSettings;
  legal_content?: LegalContentSettings;
  analytics?: AnalyticsSettings;
  [key: string]: any;
}

/**
 * V2 role catalogue surfaced by `public.roles.slug` and the
 * `get_user_organizations` / `get_user_accessible_organizations` RPCs.
 * Use alongside `ROLE_LEVEL` for level-based hierarchy gates.
 */
export type RoleSlug =
  | 'owner'
  | 'admin'
  | 'coordinator'
  | 'member'
  | 'bot-member'
  | 'report'
  | 'bot-read-only';

/**
 * Numeric `public.roles.level` for each system role slug. Mirrors the seed
 * values in `supabase/migrations/*_rbac_phase1_*` and the DB-side guards in
 * `user_max_role_level` / `assignable_role_ids_for_user`.
 */
export const ROLE_LEVEL = {
  owner: 90,
  admin: 80,
  coordinator: 70,
  member: 40,
  'bot-member': 30,
  'bot-read-only': 20,
  report: 10,
} as const satisfies Record<RoleSlug, number>;

/**
 * Minimum role level required to manage org members from the admin UI
 * (invite, change role, deactivate). Mirrors the `team.invite` permission
 * grant boundary (Owner / Admin) plus the `user_max_role_level` RLS guard.
 */
export const MANAGE_USERS_MIN_LEVEL = ROLE_LEVEL.admin;

export interface UserChildOrgAccess {
  id: string;
  user_id: string;
  parent_org_id: string;
  child_org_id: string;
  assigned_by: string | null;
  assigned_at: string;
}


export interface Organization {
  id: string;
  name: string;
  slug: string;

  // Hierarchy - for child organisations
  parent_org_id?: string | null;

  // Brand Settings
  brand_name?: string;
  brand_logo_url?: string;      // Full/wide logo for menu header
  avatar_logo_url?: string;     // Square/icon logo for tables and compact displays
  background_image_url?: string;
  favicon_url?: string;
  primary_color: string;
  secondary_color: string;
  email_background_color?: string | null;
  custom_domain?: string;

  // Contact & Settings
  contact_email?: string;
  contact_phone?: string;
  settings: OrganizationSettings;

  // Status
  status: OrganizationStatus;

  // Platform flag - true for the Penny platform admin organization
  is_platform?: boolean;

  // Landing page content - custom HTML/markdown for the organisation's landing page
  landing_page_content?: string | null;

  // Self-registration page content (child orgs) — markdown shown beside the
  // sign-up form at /customer/register/[org-slug]. Deliberately separate
  // from `landing_page_content`: registration and login address different
  // people at different moments. See migration
  // 20260804081820_add_register_page_content_to_organizations.sql.
  register_page_content?: string | null;

  // Team-branded login landing content (parent orgs only) — markdown shown
  // on /admin/login/[org-slug] above the login form. See migration
  // 20260517180000_add_team_login_landing_content_to_organizations.sql.
  team_login_landing_content?: string | null;

  // Billing plan assignment
  billing_plan_id?: string | null;

  // IP allowlist
  ip_allowlist_enabled?: boolean;

  // Metadata
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface OrganizationWithRole extends Organization {
  /** How user has access: 'direct' (explicit membership), 'inherited' (via parent org), or 'global_admin' (platform access) */
  access_type?: 'direct' | 'inherited' | 'global_admin';
  /**
   * V2 role slug sourced from `public.roles.slug` via the org RPCs
   * (`get_user_organizations` / `get_user_accessible_organizations`).
   * Use this for display and slug-based gating.
   */
  role_slug?: RoleSlug;
  /**
   * V2 numeric `public.roles.level` for level-based hierarchy gates.
   * For the synthesised global-admin org rows this is set to
   * `ROLE_LEVEL.owner` (90).
   */
  role_level?: number;
}

export interface BrandSettings {
  brand_name: string;
  brand_logo_url?: string;      // Full/wide logo for menu header
  avatar_logo_url?: string;     // Square/icon logo for tables and compact displays
  background_image_url?: string;
  favicon_url?: string;         // Browser-tab favicon (per-org, no inheritance)
  primary_color: string;
  secondary_color: string;
  email_background_color?: string | null;
  custom_domain?: string;
}

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  parent_org_id?: string | null;
  brand_name?: string;
  contact_email?: string;
  contact_phone?: string;
  primary_color?: string;
  secondary_color?: string;
}

export interface UpdateOrganizationInput {
  name?: string;
  brand_name?: string;
  brand_logo_url?: string;
  avatar_logo_url?: string;
  background_image_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  email_background_color?: string | null;
  custom_domain?: string;
  contact_email?: string;
  contact_phone?: string;
  settings?: Record<string, any>;
  status?: OrganizationStatus;
}

// Permission descriptions for user types
// global_admin = Penny Admins (platform/internal team)
// org_admin = Client Admins (organization/external users)
// Use `isPlatformAdmin` from `useAuth()` instead of inline `userType === 'global_admin'`
// checks — see RBAC v2 Phase 6 P2 (the previous `isPennyAdmin` / `isClientAdmin`
// helpers were removed in slice 6P2.4 once all call sites had migrated).
export const USER_TYPE_DESCRIPTIONS = {
  global_admin: 'Platform admin with full access to all organizations, workflows, and settings',
  org_admin: 'Organization admin with access to their assigned organization data only',
  customer: 'End user who fills in workflows via Penny',
} as const;

// ============================================================================
// Organization Features & Access Control Settings
// ============================================================================

/** Customer-facing social login providers */
export interface CustomerSocialLoginSettings {
  google: boolean;
  facebook: boolean;
  apple: boolean;
}

/** Direct referral URL configuration */
export interface DirectReferralSettings {
  enabled: boolean;
  default_workflow_id: string | null;
}

/**
 * Per-child-org data-retention (auto-anonymisation) configuration. Lives at
 * `settings.data_retention` — NOT under `settings.features` — so it is owned by
 * its dedicated audited route. IRREVERSIBLE downstream: when `enabled`, the
 * daily cron anonymises customers whose every in-scope workflow is at least
 * `retention_days` old. Disabled by default; default window is 2190 days (6y).
 */
export interface DataRetentionSettings {
  enabled: boolean;
  retention_days: number;
}

/** Default retention window in days (6 years). */
export const DEFAULT_RETENTION_DAYS = 2190;

/** Lower / upper bounds for the configurable retention window. */
export const MIN_RETENTION_DAYS = 1;
export const MAX_RETENTION_DAYS = 36500;

/** Default data-retention config: disabled, 6-year window. */
export const DEFAULT_DATA_RETENTION_SETTINGS: DataRetentionSettings = {
  enabled: false,
  retention_days: DEFAULT_RETENTION_DAYS,
};

/** Reporting feature configuration */
export interface ReportingSettings {
  enabled: boolean;
  default_dashboard_id: string | null;
}

/** Email event types from Resend webhooks */
export type EmailEventType =
  | 'email.sent'
  | 'email.delivered'
  | 'email.opened'
  | 'email.clicked'
  | 'email.bounced'
  | 'email.failed'
  | 'email.complained'
  | 'email.delivery_delayed';

/** All available email event types */
export const ALL_EMAIL_EVENT_TYPES: EmailEventType[] = [
  'email.sent',
  'email.delivered',
  'email.opened',
  'email.clicked',
  'email.bounced',
  'email.failed',
  'email.complained',
  'email.delivery_delayed',
];

/** Human-readable labels for email event types */
export const EMAIL_EVENT_LABELS: Record<EmailEventType, string> = {
  'email.sent': 'Sent',
  'email.delivered': 'Delivered',
  'email.opened': 'Opened',
  'email.clicked': 'Clicked',
  'email.bounced': 'Bounced',
  'email.failed': 'Failed',
  'email.complained': 'Complained',
  'email.delivery_delayed': 'Delivery Delayed',
};

/** Email tracking configuration for an organisation */
export interface EmailTrackingSettings {
  enabled: boolean;
  visibleEvents: EmailEventType[];
}

/** Inactivity timeout configuration */
export interface InactivityTimeoutSettings {
  timeout_minutes: number;
  warning_enabled: boolean;
  warning_before_seconds: number;
}

export const DEFAULT_INACTIVITY_TIMEOUT: InactivityTimeoutSettings = {
  timeout_minutes: 30,
  warning_enabled: true,
  warning_before_seconds: 60,
};

/** Organization-level features and access control settings */
export interface OrgFeaturesSettings {
  social_login: {
    customer: CustomerSocialLoginSettings;
  };
  direct_referral: DirectReferralSettings;
  reporting?: ReportingSettings;
  billing?: BillingSettings;
  enforce_mfa?: boolean;
  emailTracking?: EmailTrackingSettings;
  corporate_sso?: {
    enabled: boolean;
    email_domain_detection: boolean; // show email input on /admin/login for domain-based SSO routing
  };
  /** Customer session timeout — only meaningful on child organisations. */
  inactivity_timeout?: InactivityTimeoutSettings;
  /**
   * Agent/admin session timeout — only meaningful on parent organisations.
   * Resolved at runtime by `AgentInactivityGuard` from the user's parent org
   * (or the org itself when the user is already on a parent).
   */
  agent_inactivity_timeout?: InactivityTimeoutSettings;
  deactivation_messages?: DeactivationMessagesSettings;
}

/** Default organization features settings */
export const DEFAULT_ORG_FEATURES_SETTINGS: OrgFeaturesSettings = {
  social_login: {
    customer: {
      google: false,
      facebook: false,
      apple: false,
    },
  },
  direct_referral: {
    enabled: true,
    default_workflow_id: null,
  },
  reporting: {
    enabled: false,
    default_dashboard_id: null,
  },
  billing: {
    enabled: false,
  },
  enforce_mfa: false,
  emailTracking: {
    enabled: true,
    visibleEvents: ['email.sent', 'email.delivered', 'email.bounced', 'email.failed'],
  },
  corporate_sso: {
    enabled: false,
    email_domain_detection: false,
  },
  inactivity_timeout: DEFAULT_INACTIVITY_TIMEOUT,
  agent_inactivity_timeout: DEFAULT_INACTIVITY_TIMEOUT,
};

// ============================================================================
// Legal Content Settings (per-organisation)
// ============================================================================

/** Custom legal content that each organisation can maintain */
export interface LegalContentSettings {
  /** Custom privacy policy markdown content (falls back to Paylink default if empty) */
  privacy_policy?: string;
  /** Custom terms & conditions markdown content (falls back to Paylink default if empty) */
  terms_conditions?: string;
  /** Custom cookie policy markdown content (falls back to Paylink default if empty) */
  cookie_policy?: string;
  /** Custom legal footer disclaimer shown at the bottom of customer pages (falls back to Paylink default if empty) */
  footer?: string;
  /** Monotonically increasing terms version. 0 means never published. */
  terms_version?: number;
  /** ISO-8601 UTC timestamp of the last terms version bump. */
  terms_updated_at?: string;
  /** Supabase user id of the admin that published the current terms version. */
  terms_published_by?: string | null;
}

/** Default (empty) legal content — uses Paylink defaults when not set */
export const DEFAULT_LEGAL_CONTENT: LegalContentSettings = {
  privacy_policy: '',
  terms_conditions: '',
  cookie_policy: '',
  footer: '',
  terms_version: 0,
};

/** Helper to extract legal content from org settings */
export function getOrgLegalContent(settings?: Record<string, any> | null): LegalContentSettings {
  const legal = settings?.legal_content;
  const rawVersion = legal?.terms_version;
  const numericVersion = typeof rawVersion === 'number'
    ? rawVersion
    : typeof rawVersion === 'string' && rawVersion.trim() !== ''
      ? Number.parseInt(rawVersion, 10)
      : 0;

  return {
    privacy_policy: legal?.privacy_policy || '',
    terms_conditions: legal?.terms_conditions || '',
    cookie_policy: legal?.cookie_policy || '',
    footer: legal?.footer || '',
    terms_version: Number.isFinite(numericVersion) ? numericVersion : 0,
    terms_updated_at: legal?.terms_updated_at || undefined,
    terms_published_by: legal?.terms_published_by ?? null,
  };
}

/** Helper that returns the organisation's current Terms & Conditions version. */
export function getOrgTermsVersion(settings?: Record<string, any> | null): number {
  return getOrgLegalContent(settings).terms_version ?? 0;
}

// ============================================================================
// Analytics & Cookie Consent Settings (per-child-organisation) — GitLab #649
// ============================================================================

/** Consent categories. `necessary` is always granted and cannot be refused. */
export type CookieCategory =
  | 'necessary'
  | 'functional'
  | 'analytics'
  | 'marketing';

/** Ordered category list used to render the preferences panel. */
export const COOKIE_CATEGORIES: CookieCategory[] = [
  'necessary',
  'functional',
  'analytics',
  'marketing',
];

/**
 * One row of the cookie table shown inside a category (AC4/AC11). Kept as
 * free text rather than an enum because GTM can introduce a new tag — and
 * therefore a new cookie — without a deploy (D7).
 */
export interface CookieTableEntry {
  /** Cookie name as it appears in the browser, e.g. `_ga_<container>`. */
  name: string;
  /** Who sets it, e.g. "Google Analytics 4" or "Supabase". */
  provider: string;
  /** Plain-English purpose shown to the customer. */
  purpose: string;
  /** Human-readable lifetime, e.g. "2 years", "Session". */
  duration: string;
}

/** A consent category as presented to the customer. */
export interface CookieCategoryConfig {
  category: CookieCategory;
  /** Customer-facing label, e.g. "Analytics cookies". */
  label: string;
  /** Plain-English description of what the category is used for. */
  description: string;
  /**
   * `necessary` is locked on. Any other category set to `true` is
   * pre-ticked in the preferences panel — which PECR forbids, so the
   * admin UI keeps them off and the default config reflects that.
   */
  required: boolean;
  /** Cookies disclosed under this category. */
  cookies: CookieTableEntry[];
}

/** Customer-facing copy for the consent gate (AC11). */
export interface CookieBannerCopy {
  heading: string;
  body: string;
  accept_all_label: string;
  essential_only_label: string;
  manage_label: string;
  save_preferences_label: string;
}

/**
 * Per-child-org analytics and consent configuration, stored at
 * `settings.analytics`. Child orgs only — the parent org never renders a
 * consent gate, and settings are deliberately not inherited, so an
 * unconfigured child org shows no gate and loads no tags (AC3).
 */
export interface AnalyticsSettings {
  /** Master switch (AC12). Off means no gate and no GTM, whatever else is set. */
  enabled: boolean;
  /** GTM container, `GTM-XXXXXXX`. The only script the app injects itself. */
  gtm_container_id?: string;
  /** GA4 measurement ID, `G-XXXXXXXXXX`. Recorded for reference; the tag lives in GTM. */
  ga_measurement_id?: string;
  /** How long a stored consent choice stays valid (D3). */
  consent_lifetime_days: number;
  /** Bumping this invalidates every stored choice and re-prompts (D20 starts at 1). */
  consent_version: number;
  categories: CookieCategoryConfig[];
  banner: CookieBannerCopy;
}

/** Name of the first-party cookie holding the customer's choice. */
export const CONSENT_COOKIE_NAME = 'penny_cookie_consent';

/** Name of the first-party cookie holding the pre-login anonymous id (D19). */
export const CONSENT_ANONYMOUS_ID_COOKIE_NAME = 'penny_anonymous_id';

/** Default consent lifetime — 180 days (6 months), per D3. */
export const DEFAULT_CONSENT_LIFETIME_DAYS = 180;

/** Bounds for the configurable consent lifetime (1 day to 13 months). */
export const MIN_CONSENT_LIFETIME_DAYS = 1;
export const MAX_CONSENT_LIFETIME_DAYS = 395;

/** Default banner copy — plain English, no pre-ticked anything. */
export const DEFAULT_COOKIE_BANNER_COPY: CookieBannerCopy = {
  heading: 'Your privacy choices',
  body: 'Some cookies are needed to make this service work, so they cannot be turned off. We would also like to use analytics cookies to see how the service is used and fix what is not working. Nothing optional is set unless you say yes, and you can change your answer whenever you like.',
  accept_all_label: 'Accept all',
  essential_only_label: 'Essential only',
  manage_label: 'Manage preferences',
  save_preferences_label: 'Save preferences',
};

/**
 * Seed cookie table, mirroring the inventory agreed in the plan. The
 * necessary rows describe cookies the platform sets itself, so they are
 * accurate for every org; the optional rows describe the tags GTM is
 * expected to deliver and must be kept in step whenever a tag changes (D7).
 */
export const DEFAULT_COOKIE_CATEGORIES: CookieCategoryConfig[] = [
  {
    category: 'necessary',
    label: 'Strictly necessary cookies',
    description:
      'These sign you in, keep you signed in, protect forms from abuse and remember the choice you make here. The service will not work without them, so they cannot be turned off.',
    required: true,
    cookies: [
      {
        name: 'sb-<project>-auth-token',
        provider: 'Supabase',
        purpose: 'Keeps you signed in.',
        duration: 'Session lifetime',
      },
      {
        name: 'sb-<project>-auth-token-code-verifier',
        provider: 'Supabase',
        purpose: 'Secures sign-in by email link or social login.',
        duration: 'Until sign-in completes',
      },
      {
        name: 'sso_code_verifier, sso_state, sso_nonce, sso_provider_id',
        provider: 'Paylink',
        purpose: 'Secures corporate single sign-on and prevents replay attacks.',
        duration: 'Cleared on sign-in',
      },
      {
        name: 'don_referral_id',
        provider: 'Paylink',
        purpose:
          'Recovers your application after you return from your bank during open banking.',
        duration: 'Short-lived',
      },
      {
        name: 'last_admin_login_slug',
        provider: 'Paylink',
        purpose: 'Returns staff to the correct branded sign-in page.',
        duration: 'Persistent',
      },
      {
        name: '__cf_bm, cf_clearance',
        provider: 'Cloudflare',
        purpose: 'Tells humans from bots on the sign-in and registration forms.',
        duration: '30 minutes to 1 year',
      },
      {
        name: CONSENT_COOKIE_NAME,
        provider: 'Paylink',
        purpose: 'Remembers the privacy choice you make here.',
        duration: '180 days',
      },
    ],
  },
  // No `functional` entry on purpose. Nothing the app stores falls under it:
  // the theme is written only when you ask for it, `currentOrganizationId` is
  // a staff-only key and the workflow `-emitted` keys stop an event firing
  // twice — all necessary. Offering a toggle that discloses no cookies and
  // changes nothing observable invites a choice we cannot honour. The
  // category, its Consent Mode mapping and its `consent_functional` log
  // column all remain, so an org that later ships functional storage re-adds
  // it in config with no migration and no gap in the ledger.
  {
    category: 'analytics',
    label: 'Analytics cookies',
    description:
      'These show us how the service is used: which pages people visit, and where they get stuck. That includes a recording of how pages are used, with everything you see or type hidden before it leaves your device. We only use this to improve the service.',
    required: false,
    cookies: [
      {
        name: '_ga, _ga_<container>',
        provider: 'Google Analytics 4',
        purpose: 'Tells returning visits apart to count usage.',
        duration: '2 years',
      },
      {
        name: '_gid',
        provider: 'Google Analytics 4',
        purpose: 'Tells visits apart within a day.',
        duration: '24 hours',
      },
      {
        name: '_gat',
        provider: 'Google Analytics 4',
        purpose: 'Limits how often data is sent.',
        duration: '1 minute',
      },
      {
        name: '_hjSessionUser_<id>',
        provider: 'Hotjar',
        purpose: 'Recognises a returning visitor for usage analysis.',
        duration: '1 year',
      },
      {
        name: '_hjSession_<id>',
        provider: 'Hotjar',
        purpose: 'Holds the current usage-analysis session.',
        duration: '30 minutes',
      },
      // Browser storage rather than a cookie, disclosed here anyway: PECR
      // covers storing or reading anything on a device, and a customer
      // deciding whether to be recorded should not have to know the
      // difference. Nothing is stored until this category is granted.
      {
        name: 'sentryReplaySession (browser storage, not a cookie)',
        provider: 'Sentry',
        purpose:
          'Records how pages are used so we can see what went wrong when something breaks. All text, anything you type and any document you upload are hidden before the recording leaves your device.',
        duration: 'Session lifetime',
      },
    ],
  },
  {
    category: 'marketing',
    label: 'Marketing cookies',
    description:
      'These tell us whether an advert brought you here. We do not use them to build an advertising profile of you.',
    required: false,
    cookies: [
      {
        name: '_gcl_au',
        provider: 'Google Ads',
        purpose: 'Measures whether an advert led to an application.',
        duration: '90 days',
      },
    ],
  },
];

/** Default analytics config: off, nothing configured, consent version 1 (D20). */
export const DEFAULT_ANALYTICS_SETTINGS: AnalyticsSettings = {
  enabled: false,
  consent_lifetime_days: DEFAULT_CONSENT_LIFETIME_DAYS,
  consent_version: 1,
  categories: DEFAULT_COOKIE_CATEGORIES,
  banner: DEFAULT_COOKIE_BANNER_COPY,
};

/**
 * Permission required to read or change these settings, seeded by
 * `20260803180400_seed_analytics_manage_permission.sql` and granted to
 * `global-admin` only: who may configure tracking and the wording of a
 * consent notice is a data-protection decision, not a per-org one.
 *
 * Shared between the save route and the settings card so the affordance and
 * the gate cannot drift apart.
 */
export const ANALYTICS_MANAGE_PERMISSION = 'analytics.manage';

/** GTM container IDs are `GTM-` followed by an alphanumeric suffix. */
export const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,10}$/;

/** GA4 measurement IDs are `G-` followed by an alphanumeric suffix. */
export const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{6,12}$/;

function clampConsentLifetime(value: unknown): number {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number.parseInt(value, 10)
        : Number.NaN;
  if (!Number.isFinite(numeric)) return DEFAULT_CONSENT_LIFETIME_DAYS;
  return Math.min(
    MAX_CONSENT_LIFETIME_DAYS,
    Math.max(MIN_CONSENT_LIFETIME_DAYS, Math.trunc(numeric)),
  );
}

function normaliseTrackingId(value: unknown, pattern: RegExp): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toUpperCase();
  return pattern.test(trimmed) ? trimmed : undefined;
}

function normaliseCookieTable(value: unknown): CookieTableEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => ({
      name: typeof row.name === 'string' ? row.name : '',
      provider: typeof row.provider === 'string' ? row.provider : '',
      purpose: typeof row.purpose === 'string' ? row.purpose : '',
      duration: typeof row.duration === 'string' ? row.duration : '',
    }))
    .filter((row) => row.name !== '');
}

/**
 * Read the analytics block off an organisation's `settings` JSONB, filling
 * every gap from the defaults. Follows `getOrgLegalContent`: never throws,
 * so a hand-edited or partially written settings row degrades to the safe
 * default rather than breaking the customer's page.
 *
 * Deliberately does NOT walk to the parent organisation. Analytics is
 * configured per child org (AC11/AC12); inheriting a parent's container
 * would load a parent's tags on a child's journey without its consent.
 */
export function getOrgAnalyticsSettings(
  settings?: Record<string, any> | null,
): AnalyticsSettings {
  const analytics = settings?.analytics;
  if (!analytics || typeof analytics !== 'object') {
    return DEFAULT_ANALYTICS_SETTINGS;
  }

  const rawCategories = Array.isArray(analytics.categories)
    ? analytics.categories.filter(
        (entry: unknown): entry is Record<string, unknown> =>
          !!entry &&
          typeof entry === 'object' &&
          COOKIE_CATEGORIES.includes(
            (entry as Record<string, unknown>).category as CookieCategory,
          ),
      )
    : [];

  const categories: CookieCategoryConfig[] =
    rawCategories.length > 0
      ? rawCategories.map((entry: Record<string, unknown>) => {
          const category = entry.category as CookieCategory;
          const fallback =
            DEFAULT_COOKIE_CATEGORIES.find((c) => c.category === category) ??
            DEFAULT_COOKIE_CATEGORIES[0];
          return {
            category,
            label: typeof entry.label === 'string' && entry.label.trim() !== ''
              ? entry.label
              : fallback.label,
            description:
              typeof entry.description === 'string' && entry.description.trim() !== ''
                ? entry.description
                : fallback.description,
            // `necessary` is always required regardless of what is stored;
            // every other category is always optional, so a stored `true`
            // can never pre-tick a consent box (PECR).
            required: category === 'necessary',
            cookies: normaliseCookieTable(entry.cookies),
          };
        })
      : DEFAULT_COOKIE_CATEGORIES;

  const banner = analytics.banner && typeof analytics.banner === 'object'
    ? analytics.banner
    : {};

  const rawVersion = analytics.consent_version;
  const version =
    typeof rawVersion === 'number'
      ? rawVersion
      : typeof rawVersion === 'string' && rawVersion.trim() !== ''
        ? Number.parseInt(rawVersion, 10)
        : Number.NaN;

  return {
    enabled: analytics.enabled === true,
    gtm_container_id: normaliseTrackingId(
      analytics.gtm_container_id,
      GTM_CONTAINER_ID_PATTERN,
    ),
    ga_measurement_id: normaliseTrackingId(
      analytics.ga_measurement_id,
      GA_MEASUREMENT_ID_PATTERN,
    ),
    consent_lifetime_days: clampConsentLifetime(analytics.consent_lifetime_days),
    consent_version: Number.isFinite(version) && version >= 1 ? Math.trunc(version) : 1,
    categories,
    banner: {
      heading: typeof banner.heading === 'string' && banner.heading.trim() !== ''
        ? banner.heading
        : DEFAULT_COOKIE_BANNER_COPY.heading,
      body: typeof banner.body === 'string' && banner.body.trim() !== ''
        ? banner.body
        : DEFAULT_COOKIE_BANNER_COPY.body,
      accept_all_label:
        typeof banner.accept_all_label === 'string' && banner.accept_all_label.trim() !== ''
          ? banner.accept_all_label
          : DEFAULT_COOKIE_BANNER_COPY.accept_all_label,
      essential_only_label:
        typeof banner.essential_only_label === 'string' &&
        banner.essential_only_label.trim() !== ''
          ? banner.essential_only_label
          : DEFAULT_COOKIE_BANNER_COPY.essential_only_label,
      manage_label:
        typeof banner.manage_label === 'string' && banner.manage_label.trim() !== ''
          ? banner.manage_label
          : DEFAULT_COOKIE_BANNER_COPY.manage_label,
      save_preferences_label:
        typeof banner.save_preferences_label === 'string' &&
        banner.save_preferences_label.trim() !== ''
          ? banner.save_preferences_label
          : DEFAULT_COOKIE_BANNER_COPY.save_preferences_label,
    },
  };
}

/**
 * Whether the consent gate should render and GTM should load for this
 * organisation. Requires the master switch AND a valid container ID: an
 * enabled-but-unconfigured org has nothing to load, so gating the customer
 * behind a consent choice would ask them to agree to nothing (AC3).
 */
export function isAnalyticsConfigured(
  settings?: Record<string, any> | null,
): boolean {
  const analytics = getOrgAnalyticsSettings(settings);
  return analytics.enabled && !!analytics.gtm_container_id;
}

// ============================================================================
// Global/Platform Settings (Penny Admin only)
// ============================================================================

/** Admin social login providers */
export interface AdminSocialLoginSettings {
  azure: boolean;
  slack: boolean;
  gitlab: boolean;
}

/** Global reporting defaults */
export interface GlobalReportingSettings {
  default_dashboard_id: string | null;
}

/**
 * Platform-wide auth defaults. `password_reset_expiry_minutes` governs the
 * validity of recovery links generated by the slug-less admin forgot-password
 * flow (global admins). Clamped to `AUTH_CHALLENGE_LIMITS.password_reset_expiry_minutes`.
 */
export interface GlobalAuthSettings {
  password_reset_expiry_minutes: number;
}

/** Platform-wide global settings */
export interface GlobalPlatformSettings {
  social_login: {
    admin: AdminSocialLoginSettings;
  };
  reporting?: GlobalReportingSettings;
  auth?: GlobalAuthSettings;
}

/** Default global platform settings */
export const DEFAULT_GLOBAL_SETTINGS: GlobalPlatformSettings = {
  social_login: {
    admin: {
      azure: false,
      slack: false,
      gitlab: false,
    },
  },
  reporting: {
    default_dashboard_id: null,
  },
  auth: {
    password_reset_expiry_minutes:
      DEFAULT_AUTH_CHALLENGE_SETTINGS.password_reset_expiry_minutes,
  },
};



// ============================================================================
// Corporate SSO Provider Types
// ============================================================================

/** Supported SSO provider types */
export type SSOProviderType = 'azure_ad' | 'google_workspace' | 'okta' | 'ping_identity' | 'saml' | 'oidc';

/**
 * SSO provider configuration for an organisation.
 *
 * `default_role_id` is a UUID FK into `public.roles` (RBAC v2 catalogue).
 * `default_role` is the resolved system-role slug (e.g. `'member'`, `'admin'`)
 * surfaced for UI/JSON convenience — populated server-side by joining onto
 * `public.roles`. Both fields refer to the same role.
 */
export interface OrgSSOProvider {
  id: string;
  organization_id: string;
  provider_type: SSOProviderType;
  display_name: string;
  enabled: boolean;
  client_id: string;
  // client_secret_encrypted is never sent to the frontend
  issuer_url?: string;
  domain: string;
  metadata_url?: string;
  scopes: string;
  auto_provision: boolean;
  default_role_id: string;
  default_role: string;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Input for the admin UI create/edit form (secret sent on create only).
 *
 * `default_role` is a system-role slug from the v2 catalogue (e.g. `'member'`).
 * The server resolves this to a `default_role_id` before insert.
 */
export interface OrgSSOProviderInput {
  provider_type: SSOProviderType;
  display_name: string;
  enabled?: boolean;
  client_id: string;
  client_secret?: string; // plaintext, encrypted on server
  issuer_url?: string;
  domain: string;
  metadata_url?: string;
  scopes?: string;
  auto_provision?: boolean;
  default_role?: string;
}

/** Human-readable labels for SSO provider types */
export const SSO_PROVIDER_LABELS: Record<SSOProviderType, string> = {
  azure_ad: 'Azure AD / Entra ID',
  google_workspace: 'Google Workspace',
  okta: 'Okta',
  ping_identity: 'Ping Identity',
  saml: 'SAML 2.0 (Generic)',
  oidc: 'OIDC (Generic)',
};

// ============================================================================
// IP Allowlist Types
// ============================================================================

/** A single IP allowlist entry for an organisation */
export interface OrgIPAllowlistEntry {
  id: string;
  organization_id: string;
  ip_or_cidr: string;
  label?: string;
  created_at: string;
  created_by?: string;
}
